/**
 * «إضبارة جديدة» — the data side of the new-dossier page: empty forms,
 * the quick-pick lists, name composition, validation, and the look-ups
 * that warn about a pupil or a family that is already registered.
 *
 * The quick picks are the values the office actually writes in its
 * registration workbook (سجل 27.xlsx, ~1,200 rows), most used first, so
 * a new dossier reads like the existing ones.
 */
import { normalize } from '../../search/searchEngine';
import { normalizePhone } from '../../../ui/phone';

export const emptyFamily = {
  father_name: '', last_name: '', father_phone: '', father_job: '',
  mother_name: '', mother_last_name: '', mother_phone: '',
  amana: '', address: '',
};

export const emptyStudent = {
  first_name: '', grandfather_name: '', gender: '', birth_date: '', birth_place: '',
  national_id: '', student_phone: '',
  id_document_type: '', registry_place: '', registry_number: '',
  displacement_status: '', origin_place: '', photos: '',
  family_status: '', health_status: '',
  section_id: '', registration_date: '',
  tuition_fee: '', transportation_id: '', transport_fee: '',
  notes: '',
};

/** A fresh draft: new family (the default), today's registration date. */
export function freshDraft() {
  return {
    familyMode: 'new',
    familyId: '',
    family: { ...emptyFamily },
    student: { ...emptyStudent, registration_date: localToday() },
    // The full name is composed from the pieces until the office types
    // in it; from then on it is theirs (same rule as the الطلاب form).
    name: '',
    nameTouched: false,
  };
}

// --- quick picks ---------------------------------------------------------

/** الأوراق الثبوتية — joined with « + » like the workbook («هوية + دفتر عائلة»). */
export const ID_DOCS = ['هوية', 'دفتر عائلة', 'إخراج قيد', 'بيان عائلي', 'جواز سفر', 'شهادة ولادة'];

/** الجلاء / الوثيقة الدراسية. */
export const SCHOOL_DOCS = ['جلاء', 'وثيقة طالب', 'شهادة تاسع', 'إفادة إنهاء', 'صورة طبق الأصل', 'تسلسل دراسي'];

export const FAMILY_STATUS = ['الأب والأم على قيد الحياة', 'يتيم الأب', 'يتيم الأم', 'يتيم الأب والأم'];
export const HEALTH_STATUS = ['سليم', 'لديه احتياجات خاصة', 'مريض مزمن'];

export const PLACES = ['اعزاز', 'حلب', 'تلرفعت', 'معرة النعمان', 'تلدبس', 'عفرين', 'السلامة', 'مارع', 'منق', 'ادلب', 'دير الزور', 'اريحا', 'تركيا'];
export const AMANA = ['اعزاز', 'تل رفعت', 'معرة النعمان', 'حلب', 'حريتان', 'مارع', 'اخترين', 'دير حافر', 'السفيرة', 'صوران'];
export const REGISTRY_PLACES = ['تلرفعت', 'شمالية', 'منق', 'شرقية', 'تلدبس', 'قبلية', 'غربية', 'كلجبرين'];
export const ORIGINS = ['المتميزون', 'تركيا', 'العالمية', 'لبنان', 'الأقصى', 'خالد بن الوليد', 'ميسون', 'خديجة الكبرى', 'الحكمة', 'النخبة'];
export const NOTE_CHIPS = ['يوجد تعهد', 'معيد'];

/** Toggle one token inside a « + » list («هوية + دفتر عائلة»). */
export function toggleToken(text, token) {
  const parts = splitTokens(text);
  const at = parts.findIndex((p) => normalize(p) === normalize(token));
  if (at >= 0) parts.splice(at, 1);
  else parts.push(token);
  return parts.join(' + ');
}

export function hasToken(text, token) {
  return splitTokens(text).some((p) => normalize(p) === normalize(token));
}

function splitTokens(text) {
  return String(text || '').split(/\s*[+،,]\s*/).map((s) => s.trim()).filter(Boolean);
}

/** A notes line added or removed as a whole («يوجد تعهد»). */
export function toggleNoteLine(notes, line) {
  const lines = String(notes || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const at = lines.indexOf(line);
  if (at >= 0) lines.splice(at, 1); else lines.push(line);
  return lines.join('\n');
}

// --- names -----------------------------------------------------------------

const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim();

/**
 * The family's surname. Most imported families have no last_name on
 * file (the workbook kept it per pupil), so like the API's
 * familySurname() it falls back to the words after the father's name in
 * a sibling's name: «يوسف أحمد الحلبي» + father «أحمد» → «الحلبي».
 */
export function familySurname(family) {
  if (!family) return '';
  if (clean(family.last_name)) return clean(family.last_name);
  const first = clean(String(family.student_names || '').split('،')[0]);
  const father = clean(family.father_name);
  if (!first) return '';
  if (father && first.includes(` ${father} `)) {
    return clean(first.slice(first.indexOf(` ${father} `) + father.length + 2));
  }
  const words = first.split(' ');
  return words.length > 1 ? words[words.length - 1] : '';
}

/** الاسم + اسم الأب + الكنية — the name the school writes everywhere. */
export function composeName(firstName, family) {
  return [firstName, family?.father_name, familySurname(family)]
    .map(clean)
    .filter(Boolean)
    .join(' ');
}

export const initials = (name) => {
  const w = clean(name).split(' ').filter(Boolean);
  if (!w.length) return '؟';
  return w.length >= 2 ? w[0][0] + w[1][0] : w[0][0];
};

// --- dates -------------------------------------------------------------------

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Today on the office computer's own calendar (not UTC). */
export function localToday() {
  return isoDay(new Date());
}

/** «7 سنوات و3 أشهر» from a YYYY-MM-DD birth date, or ''. */
export function ageText(birth) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birth || '')) return '';
  const [y, m, d] = birth.split('-').map(Number);
  const now = new Date();
  let months = (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
  if (now.getDate() < d) months -= 1;
  if (months < 0) return '';
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const yWord = years === 1 ? 'سنة' : years === 2 ? 'سنتان' : years <= 10 ? `${years} سنوات` : `${years} سنة`;
  const mWord = rest === 1 ? 'شهر' : rest === 2 ? 'شهران' : `${rest} أشهر`;
  if (!years) return mWord;
  return rest ? `${yWord} و${mWord}` : yWord;
}

// --- validation ----------------------------------------------------------------

const PHONE_FIELDS = [
  ['family', 'father_phone', 'هاتف الأب'],
  ['family', 'mother_phone', 'هاتف الأم'],
  ['student', 'student_phone', 'هاتف الطالب'],
];

/**
 * Errors that block «تأكيد», keyed by field ('family.father_name', …).
 * Only what the server would refuse or what makes the dossier useless:
 * everything else is optional and shows up in the completeness list.
 */
export function validate(draft) {
  const e = {};
  const { family, student } = draft;
  if (!clean(student.first_name) && !clean(draft.name)) e['student.first_name'] = 'اسم الطالب مطلوب';
  if (!clean(draft.name)) e.name = 'الاسم الكامل مطلوب';
  if (draft.familyMode === 'new') {
    if (!clean(family.father_name) && !clean(family.mother_name)) {
      e['family.father_name'] = 'اكتب اسم الأب أو اسم الأم';
    }
  } else if (!draft.familyId) {
    e.familyId = 'اختر العائلة';
  }
  for (const [group, key, label] of PHONE_FIELDS) {
    if (group === 'family' && draft.familyMode !== 'new') continue;
    const v = draft[group][key];
    if (v && !normalizePhone(v)) e[`${group}.${key}`] = `${label} غير صحيح`;
  }
  if (student.birth_date && student.birth_date > localToday()) e['student.birth_date'] = 'تاريخ الميلاد في المستقبل';
  if (student.tuition_fee !== '' && Number(student.tuition_fee) < 0) e['student.tuition_fee'] = 'قيمة غير صحيحة';
  return e;
}

/** What is still missing from a complete dossier (does not block). */
export function missingItems(draft, family) {
  const s = draft.student;
  const items = [
    ['student.gender', 'الجنس', !!s.gender],
    ['student.birth_date', 'تاريخ الميلاد', !!s.birth_date],
    ['student.birth_place', 'مكان الولادة', !!clean(s.birth_place)],
    ['student.national_id', 'الرقم الوطني', !!clean(s.national_id)],
    ['student.id_document_type', 'الأوراق الثبوتية', !!clean(s.id_document_type)],
    ['student.registry_place', 'محل ورقم القيد', !!clean(s.registry_place) && !!clean(s.registry_number)],
    ['student.section_id', 'الشعبة', !!s.section_id],
    ['family.father_phone', 'هاتف الأب أو الأم', !!(family?.father_phone || family?.mother_phone)],
    ['family.address', 'العنوان', !!clean(family?.address)],
  ];
  const done = items.filter((i) => i[2]).length + 1; // + the name, always there by now
  return {
    percent: Math.round((done / (items.length + 1)) * 100),
    missing: items.filter((i) => !i[2]).map(([field, label]) => ({ field, label })),
  };
}

// --- "already registered?" --------------------------------------------------------

/** Families that look like the one being typed: same phone, or same father + surname. */
export function matchFamilies(family, families) {
  if (!families?.length) return [];
  const phones = [family.father_phone, family.mother_phone].map(normalizePhone).filter(Boolean);
  const father = normalize(clean(family.father_name));
  const surname = normalize(clean(family.last_name));
  const mother = normalize(clean(family.mother_name));
  const out = [];
  for (const f of families) {
    const fPhones = [f.father_phone, f.mother_phone].map(normalizePhone).filter(Boolean);
    const phoneHit = phones.some((p) => fPhones.includes(p));
    const nameHit = father && surname
      && normalize(clean(f.father_name)) === father
      && normalize(familySurname(f)) === surname
      && (!mother || !f.mother_name || normalize(clean(f.mother_name)) === mother);
    if (phoneHit || nameHit) out.push({ family: f, by: phoneHit ? 'phone' : 'name' });
    if (out.length >= 3) break;
  }
  return out;
}

/** Pupils already on file with this national id, or exactly this name. */
export function matchStudents(draft, students) {
  if (!students?.length) return { byNid: [], byName: [] };
  const nid = clean(draft.student.national_id).replace(/\s/g, '');
  const name = normalize(clean(draft.name));
  return {
    byNid: nid.length >= 5 ? students.filter((s) => clean(s.national_id).replace(/\s/g, '') === nid).slice(0, 3) : [],
    byName: name.split(' ').length >= 2 ? students.filter((s) => normalize(clean(s.name)) === name).slice(0, 3) : [],
  };
}

// --- the request ---------------------------------------------------------------------

/** The POST /students body for a draft (see api people.js createStudent). */
export function toRequest(draft) {
  const s = draft.student;
  const notes = [clean(s.notes) ? s.notes.trim() : '', Number(s.photos) > 0 ? `صور شخصية: ${Number(s.photos)}` : '']
    .filter(Boolean).join('\n');
  const body = {
    name: clean(draft.name),
    first_name: clean(s.first_name) || null,
    grandfather_name: clean(s.grandfather_name) || null,
    gender: s.gender || null,
    birth_date: s.birth_date || null,
    birth_place: clean(s.birth_place) || null,
    national_id: clean(s.national_id) || null,
    student_phone: s.student_phone || null,
    id_document_type: clean(s.id_document_type) || null,
    registry_place: clean(s.registry_place) || null,
    registry_number: clean(s.registry_number) || null,
    displacement_status: clean(s.displacement_status) || null,
    origin_place: clean(s.origin_place) || null,
    family_status: s.family_status || null,
    health_status: clean(s.health_status) || null,
    registration_date: s.registration_date || null,
    tuition_fee: Number(s.tuition_fee) || 0,
    transport_fee: s.transportation_id ? undefined : Number(s.transport_fee) || 0,
    transportation_id: s.transportation_id ? Number(s.transportation_id) : null,
    section_id: s.section_id ? Number(s.section_id) : null,
    notes: notes || null,
  };
  if (draft.familyMode === 'new') {
    const f = draft.family;
    body.family = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, clean(v) || null]));
  } else {
    body.family_id = Number(draft.familyId);
  }
  return body;
}
